import type { JSX } from "react";
import type { MeetingSummaryBlock as MeetingSummaryBlockModel } from "@shared/types";
import type { BlockRendererProps } from "./registry";

/**
 * Meeting-summary block (openspec/meeting-capture.md §7; uiux §6/§10.5). Renders
 * the 5-part summary in CDS tokens, then the AI-native layer: assistant-proposed
 * next-step chips + a persistent "ask this meeting" input — both send a prompt
 * via onSendPrompt (the same freeform-button → onSendPrompt seam the suggestion-
 * first redesign uses), so the meeting is a living context, not a static archive.
 *
 * PH-1 (MEETING_SESSION): when `askInSession` is provided (MeetingView passes it
 * when the meeting owns a real conversation), chips send ONLY the instruction —
 * the summary is already the session's seeded context, so we don't re-stuff it.
 * Without it (main-chat registry render), the legacy meetingContext prefix stays.
 */
export function MeetingSummaryBlockView({ block, onSendPrompt, askInSession }: BlockRendererProps & { askInSession?: (instruction: string) => void }): JSX.Element {
  const m = block as MeetingSummaryBlockModel;
  const ask = (instruction: string): void => {
    if (askInSession !== undefined) askInSession(instruction);
    else onSendPrompt(`${meetingContext(m)}\n\n${instruction}`);
  };
  return (
    <div className="meeting-summary" data-meeting-id={m.meetingId}>
      {m.exec.length > 0 && <p className="ms-exec">{m.exec}</p>}

      {m.forYou.length > 0 && (
        <section className="ms-section">
          <h4 className="ms-h">For you</h4>
          <ul className="ms-list">
            {m.forYou.map((i, k) => (
              <li key={k} className="ms-item">
                <span className={`ms-state ms-state--${i.state}`} aria-hidden="true" />
                <span className="ms-text">{i.text}</span>
                {i.owner.length > 0 && <span className="ms-owner">{i.owner}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {m.decisions.length > 0 && (
        <section className="ms-section">
          <h4 className="ms-h">Decisions</h4>
          <ul className="ms-list">
            {m.decisions.map((d, k) => (
              <li key={k} className="ms-item ms-item--decision">
                <span className="ms-mark ms-mark--dec" aria-hidden="true">✓</span>
                <span className="ms-body">
                  <span className="ms-text">{d.text}</span>
                  {d.quote.length > 0 && (
                    <span className="ms-quote">“{d.quote}”{d.speaker.length > 0 ? ` — ${d.speaker}` : ""}{d.ts.length > 0 ? `, ${d.ts}` : ""}</span>
                  )}
                </span>
                {d.suggestion.length > 0 && (
                  <button type="button" className={`ms-sugg ms-sugg--${d.suggestion}`} onClick={() => ask(`把这条会议决策「${d.text}」变成一个${suggLabel(d.suggestion)}，并给出下一步。`)}>
                    {suggLabel(d.suggestion)}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {m.actionsOthers.length > 0 && (
        <section className="ms-section">
          <h4 className="ms-h">Action items · others</h4>
          <ul className="ms-list">
            {m.actionsOthers.map((a, k) => (
              <li key={k} className="ms-item">
                <span className="ms-mark ms-mark--act" aria-hidden="true" />
                <span className="ms-text">{a.text}</span>
                {a.owner.length > 0 && <span className="ms-owner">{a.owner}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {m.risks.length > 0 && (
        <section className="ms-section">
          <h4 className="ms-h">Risks &amp; open questions</h4>
          <ul className="ms-list">
            {m.risks.map((r, k) => (
              <li key={k} className="ms-item">
                <span className="ms-mark ms-mark--risk" aria-hidden="true">!</span>
                <span className="ms-text">{r.text}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {m.nextSteps.length > 0 && (
        <div className="ms-nextsteps">
          <div className="ms-nextsteps-lab">✦ 小环读完这场会，建议的下一步</div>
          <div className="ms-nextsteps-row">
            {m.nextSteps.map((s, k) => (
              <button key={k} type="button" className="ms-chip" onClick={() => ask(s.prompt)}>
                {s.label}<span className="ms-chip-arw" aria-hidden="true">→</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function suggLabel(s: string): string {
  switch (s) {
    case "task": return "任务";
    case "routine": return "例行";
    case "skill": return "技能";
    case "research": return "调研";
    default: return "任务";
  }
}

/**
 * Serialize the summary into a text context block. AI-native living context:
 * this travels with every chip / ask prompt so the agent answers "问这场会"
 * grounded in the actual meeting — without a conversation-store round-trip.
 */
export function meetingContext(m: MeetingSummaryBlockModel): string {
  const out = [`【会议「${m.title}」纪要】`];
  if (m.exec.trim().length > 0) out.push(`摘要：${m.exec}`);
  if (m.decisions.length > 0)
    out.push("决策：\n" + m.decisions.map((d) => `- ${d.text}${d.quote.length > 0 ? `（原话“${d.quote}”${d.speaker.length > 0 ? ` — ${d.speaker}` : ""}${d.ts.length > 0 ? `, ${d.ts}` : ""}）` : ""}`).join("\n"));
  if (m.forYou.length > 0) out.push("我的待办：\n" + m.forYou.map((i) => `- ${i.text}`).join("\n"));
  if (m.actionsOthers.length > 0) out.push("他人待办：\n" + m.actionsOthers.map((a) => `- ${a.text}${a.owner.length > 0 ? `（${a.owner}）` : ""}`).join("\n"));
  if (m.risks.length > 0) out.push("风险与遗留：\n" + m.risks.map((r) => `- ${r.text}`).join("\n"));
  return out.join("\n");
}
